import { useState } from 'react';
import { Box, Typography, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { Trash2, Check, X } from 'lucide-react';
import type { ChatListing } from 'types';

interface HistoryPanelProps {
  chatHistory: ChatListing[];
  activeChatId: string | null;
  onSelectChat: (id: string) => void;
  onDeleteChat: (id: string) => void;
  deletingChatId: string | null;
}

function formatDate(ts: number): string {
  const date = new Date(ts * 1000);
  const now = new Date();
  // Считаем разницу по календарным дням, а не по 24-часовым интервалам:
  // иначе чат, созданный вчера вечером, утром ошибочно помечается как «Сегодня».
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((startOfToday.getTime() - startOfDate.getTime()) / 86400000);

  if (diffDays === 0) {
    return `Сегодня, ${date.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}`;
  }
  if (diffDays === 1) {
    return `Вчера, ${date.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}`;
  }
  const dayMonth = date.toLocaleDateString('ru', { day: 'numeric', month: 'short' });
  const time = date.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
  return `${dayMonth}, ${time}`;
}

function chatTitle(chat: ChatListing): string {
  if (chat.title) {
    return chat.title.length > 40 ? chat.title.slice(0, 40) + '…' : chat.title;
  }
  return formatDate(chat.created_at);
}

export default function HistoryPanel({
  chatHistory,
  activeChatId,
  onSelectChat,
  onDeleteChat,
  deletingChatId,
}: HistoryPanelProps) {
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  return (
    <Box
      sx={{
        width: 260,
        flexShrink: 0,
        bgcolor: 'var(--color-surface)',
        borderLeft: '1px solid var(--color-border)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid var(--color-border)' }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: 'var(--color-text-muted)',
          }}
        >
          История
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {chatHistory.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 12 }}>
            История пуста
          </Box>
        ) : (
          chatHistory.map((chat) => {
            const isSelected = activeChatId === chat.id;
            const isConfirming = confirmingId === chat.id;
            const isDeleting = deletingChatId === chat.id;
            return (
              <Box
                key={chat.id}
                onClick={() => !isConfirming && !isDeleting && onSelectChat(chat.id)}
                sx={{
                  position: 'relative',
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid var(--color-border)',
                  cursor: isConfirming || isDeleting ? 'default' : 'pointer',
                  transition: 'background 0.1s',
                  ...(isSelected
                    ? {
                        bgcolor: 'rgba(20,116,255,0.08)',
                        borderLeft: '3px solid var(--color-primary)',
                        pl: '13px',
                      }
                    : {}),
                  '&:hover': {
                    bgcolor: isSelected ? 'rgba(20,116,255,0.08)' : 'var(--color-bg)',
                    '& .history-delete-btn': { opacity: 1 },
                  },
                }}
              >
                <Box sx={{ pr: 3.5 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      mb: 0.5,
                      lineHeight: 1.4,
                      wordBreak: 'break-word',
                    }}
                  >
                    {chatTitle(chat)}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                    {formatDate(chat.created_at)}
                  </Typography>
                </Box>

                {/* Действия удаления */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    right: 6,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    ...(isConfirming
                      ? {
                          bgcolor: 'var(--color-surface)',
                          borderRadius: 1,
                          px: 0.5,
                          boxShadow: '0 0 0 1px var(--color-border)',
                        }
                      : {}),
                  }}
                >
                  {isDeleting ? (
                    <CircularProgress size={14} sx={{ color: 'var(--color-text-muted)', m: 0.5 }} />
                  ) : isConfirming ? (
                    <>
                      <Tooltip title="Удалить">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmingId(null);
                            onDeleteChat(chat.id);
                          }}
                          sx={{
                            width: 24,
                            height: 24,
                            color: '#dc2626',
                            '&:hover': { bgcolor: 'rgba(220,38,38,0.1)' },
                          }}
                        >
                          <Check size={14} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Отмена">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmingId(null);
                          }}
                          sx={{
                            width: 24,
                            height: 24,
                            color: 'var(--color-text-muted)',
                            '&:hover': { bgcolor: 'var(--color-bg)' },
                          }}
                        >
                          <X size={14} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip title="Удалить чат">
                      <IconButton
                        className="history-delete-btn"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmingId(chat.id);
                        }}
                        sx={{
                          width: 24,
                          height: 24,
                          opacity: 0,
                          transition: 'opacity 0.1s',
                          color: 'var(--color-text-muted)',
                          '&:hover': {
                            bgcolor: 'rgba(220,38,38,0.1)',
                            color: '#dc2626',
                          },
                        }}
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
